/**
 * Hook for subscribing to current user's Firestore document
 * Provides real-time user data including plan and usage
 */

import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/config/firebase";
import { useEffectiveUserId } from "./useEffectiveUserId";
import type { User } from "@/types";

export function useUserData() {
  const { effectiveUid } = useEffectiveUserId();
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!effectiveUid) {
      setUserData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      doc(db, "users", effectiveUid),
      (docSnap) => {
        if (docSnap.exists()) {
          setUserData(docSnap.data() as User);
        } else {
          setUserData(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to user document:", err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [effectiveUid]);

  return { userData, loading, error };
}
